import cx from 'clsx';
import React from 'react';
import { type Control, useFieldArray, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as RemoveIcon } from '@assets/svg/ic-remove.svg';

import EditorForm from '@components/form/EditorForm/EditorForm';
import Select from '@components/uikit/Select/Select';

import useTranslation from '@hooks/useTranslation';

import { COMPLETION_REPORT_FIELDS } from '../constants/schemaCompletionReport';
import { getCauseCategoriesList } from '../helpers/getCauseCategoriesList';
import { getSeverityLevelList } from '../helpers/getSeverityLevelList';
import AttachmentsField from './AttachmentsField/AttachmentsField';
import style from './style.module.scss';

interface IProps {
  noActionsSwitcher: number;
  control: Control;
}

const emptyItem = { severity: { id: null }, cause_category: { value: null } };

const StepMaintenanceDetails: React.FC<IProps> = ({ noActionsSwitcher, control }) => {
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    name: COMPLETION_REPORT_FIELDS.CAUSE_CATEGORY_SEVERITIES,
    control,
  });

  const causeCategoriesSeverities = useWatch({
    control,
    name: COMPLETION_REPORT_FIELDS.CAUSE_CATEGORY_SEVERITIES,
  });

  const severityLevelList = getSeverityLevelList(t);
  const { causeCategoriesList, listLength } = getCauseCategoriesList(t, causeCategoriesSeverities);

  return (
    <div className={cx(style.stepMainDetailsInfo, style.scrollingBlock)}>
      {noActionsSwitcher === 0 && (
        <>
          <div>
            {fields.map((part, i) => (
              <div key={uuidv4()} className={style.mainDetails}>
                <Select
                  name={`${COMPLETION_REPORT_FIELDS.CAUSE_CATEGORY_SEVERITIES}[${i}][cause_category]`}
                  control={control}
                  label={t(i === 0 ? 'Cause Category' : '')}
                  options={causeCategoriesList}
                  itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
                  required
                />
                <Select
                  name={`${COMPLETION_REPORT_FIELDS.CAUSE_CATEGORY_SEVERITIES}[${i}][severity]`}
                  control={control}
                  label={t(i === 0 ? 'Severity level' : '')}
                  options={severityLevelList}
                  itemKeys={{ valueFieldKey: 'id', labelFieldKey: 'name' }}
                  required
                />
                {fields.length > 1 && (
                  <RemoveIcon className={style.removePartIcon} onClick={() => remove(i)} />
                )}
              </div>
            ))}
            <div className={style.separator} />
            <div className={style.addMoreBtnWrapper}>
              <span
                className={cx(style.addMoreBtn, { [style.addMoreBtnDisabled]: listLength === fields.length })}
                onClick={() => append(emptyItem)}
              >
                + {t('Add additional cause categories')}
              </span>
            </div>
          </div>
          <EditorForm
            name={COMPLETION_REPORT_FIELDS.CAUSE}
            control={control}
            label="Description of Cause"
            required
            dataQa={COMPLETION_REPORT_FIELDS.CAUSE}
          />
        </>
      )}
      <AttachmentsField name={COMPLETION_REPORT_FIELDS.FILE_NAME} control={control} label="Attachments" />
    </div>
  );
};

export default StepMaintenanceDetails;
